// File parsing utilities for member import
// This module provides functions to parse CSV, Excel, and vCard files

/**
 * Extract phone number from text with better international support
 * @param {string} text - Text containing phone number
 * @returns {Object|null} Object with number and countryCode, or null
 */
const extractPhoneNumber = (text) => {
  // Enhanced phone regex patterns for various international formats
  const phonePatterns = [
    // International format with country code (most specific first)
    /\+\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{3,4}[-.\s]?\d{3,4}/g,
    // US/Canada format variations
    /(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})/g,
    // International format without parentheses but with separators
    /(?:\+\d{1,4}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)(\d{3,4}[-.\s]?\d{3,4})/g,
    // European formats with various separators
    /(?:\+\d{1,4}[-.\s]?)?\d{2,4}[-.\s]?\d{3,4}[-.\s]?\d{3,4}/g,
    // Israeli mobile format specifically (05X-XXX-XXXX or 05XXXXXXXX)
    /0?5[0-9][-.\s]?\d{3}[-.\s]?\d{4}/g,
    // Simple digit groups (7+ digits) - most permissive, last resort
    /\b\d{7,15}\b/g,
    // Handle cases where the entire line might be just digits (phone only)
    /^\s*\+?\d{7,15}\s*$/g
  ];

  // First try to find the best match (most structured)
  for (const pattern of phonePatterns) {
    const matches = text.match(pattern);
    if (matches) {
      for (const match of matches) {
        const cleaned = match.replace(/[^\d+]/g, '');
        
        // Skip if it's too short or too long to be a phone number
        const digitsOnly = cleaned.replace('+', '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) {
          continue;
        }
        
        // Skip obviously invalid patterns (like all same digits)
        if (/^(\d)\1+$/.test(digitsOnly)) {
          continue;
        }
        
        // Extract country code if present
        let countryCode = null;
        let number = cleaned;
        
        if (cleaned.startsWith('+')) {
          const countryMatch = cleaned.match(/^\+(\d{1,4})/);
          if (countryMatch) {
            countryCode = countryMatch[1];
            number = cleaned.substring(countryMatch[0].length);
          }
        }
        
        return { number: cleaned, countryCode, originalMatch: match };
      }
    }
  }
  
  return null;
};

/**
 * Detect country from phone number patterns and formatting
 * @param {string} originalText - Original text containing the phone number
 * @param {string} phoneNumber - Extracted phone number
 * @returns {string|null} Detected country code or null
 */
const detectCountryFromPhonePattern = (originalText, phoneNumber) => {
  const cleaned = phoneNumber.replace(/[^\d]/g, '');
  
  // Look for country indicators in the original text
  const textLower = originalText.toLowerCase();
  
  // Check for explicit country indicators
  if (textLower.includes('israel') || textLower.includes('israeli') || textLower.includes('il')) {
    return '972';
  }
  if (textLower.includes('uk') || textLower.includes('britain') || textLower.includes('england')) {
    return '44';
  }
  if (textLower.includes('germany') || textLower.includes('german') || textLower.includes('de')) {
    return '49';
  }
  if (textLower.includes('france') || textLower.includes('french') || textLower.includes('fr')) {
    return '33';
  }
  if (textLower.includes('australia') || textLower.includes('australian') || textLower.includes('au')) {
    return '61';
  }
  if (textLower.includes('canada') || textLower.includes('canadian')) {
    return '1';
  }
  if (textLower.includes('usa') || textLower.includes('america') || textLower.includes('us')) {
    return '1';
  }
  
  // Analyze number patterns for country detection
  if (cleaned.length === 10) {
    // Could be multiple countries - look for formatting clues
    
    // Israeli mobile numbers often start with 05
    if (cleaned.startsWith('05')) {
      return '972';
    }
    
    // US numbers starting with certain patterns
    if (cleaned.match(/^[2-9]\d{2}[2-9]\d{6}$/)) {
      // Valid US format (area code 200-999, exchange 200-999)
      return '1';
    }
    
    // French mobile numbers start with 06 or 07
    if (cleaned.startsWith('06') || cleaned.startsWith('07')) {
      return '33';
    }
    
    // UK mobile numbers start with 07
    if (cleaned.startsWith('07')) {
      return '44';
    }
  }
  
  if (cleaned.length === 9) {
    // Israeli landline without leading 0
    if (cleaned.match(/^[2-9]/)) {
      return '972';
    }
  }
  
  if (cleaned.length === 11) {
    // Could be US with 1, or other countries
    if (cleaned.startsWith('1')) {
      return '1';
    }
  }
  
  return null;
};

/**
 * Normalize phone number to E.164 format with country context
 * @param {string} phone - Raw phone number
 * @param {string} countryCode - Country code if detected
 * @param {string} originalText - Original text for additional context
 * @returns {string} E.164 formatted phone number
 */
const normalizePhoneToE164 = (phone, countryCode = null, originalText = '') => {
  // Remove all non-digits except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // If already in E.164 format and valid length, return as-is
  if (cleaned.startsWith('+') && cleaned.length >= 8 && cleaned.length <= 16) {
    return cleaned;
  }
  
  // Remove leading + for processing
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }
  
  // Try to detect country from patterns if not provided
  if (!countryCode && originalText) {
    countryCode = detectCountryFromPhonePattern(originalText, cleaned);
  }
  
  // Handle numbers by length and pattern
  if (cleaned.length === 10) {
    if (countryCode) {
      return `+${countryCode}${cleaned}`;
    }
    
    // Default behavior - try to determine based on pattern
    if (cleaned.startsWith('05')) {
      // Likely Israeli mobile
      return `+972${cleaned}`;
    } else if (cleaned.startsWith('06') || cleaned.startsWith('07')) {
      // Could be French or UK mobile
      if (cleaned.startsWith('07') && originalText.toLowerCase().includes('uk')) {
        return `+44${cleaned}`;
      } else if (cleaned.startsWith('06') || cleaned.startsWith('07')) {
        return `+33${cleaned}`;
      }
    } else if (cleaned.match(/^[2-9]\d{2}[2-9]\d{6}$/)) {
      // Valid US format pattern
      return `+1${cleaned}`;
    }
    
    // If we can't determine, return without country code and let user decide
    return `+${cleaned}`;
    
  } else if (cleaned.length === 9) {
    if (countryCode === '972' || cleaned.match(/^[2-9]/)) {
      // Israeli landline
      return `+972${cleaned}`;
    }
    
  } else if (cleaned.length === 11) {
    if (cleaned.startsWith('1')) {
      // US/Canada number
      return `+${cleaned}`;
    } else if (countryCode) {
      return `+${countryCode}${cleaned}`;
    }
    
  } else if (cleaned.length === 12) {
    // Check common 12-digit patterns
    if (cleaned.startsWith('44')) {
      return `+${cleaned}`;
    } else if (cleaned.startsWith('972')) {
      return `+${cleaned}`;
    } else if (countryCode) {
      return `+${countryCode}${cleaned}`;
    }
  }
  
  // For other cases, use provided country code or return as-is with +
  if (countryCode && !cleaned.startsWith(countryCode)) {
    return `+${countryCode}${cleaned}`;
  }
  
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
};

/**
 * Parse text/paste input
 * @param {string} text - Pasted text content
 * @returns {Array} Array of member objects
 */
export const parseText = (text) => {
  const lines = text.split('\n').filter(line => line.trim());
  const members = [];
  
  lines.forEach((line, index) => {
    const member = parseTextLine(line, index);
    if (member) {
      members.push(member);
    }
  });
  
  return members;
};

/**
 * Parse a single text line to extract member info
 * @param {string} line - Text line to parse
 * @param {number} index - Line index for ID generation
 * @returns {Object|null} Member object or null if invalid
 */
const parseTextLine = (line, index) => {
  const trimmedLine = line.trim();
  const member = {
    id: `text-${index}`,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    rawText: trimmedLine
  };
  
  // Extract email
  const emailMatch = trimmedLine.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
  if (emailMatch) {
    member.email = emailMatch[0];
  }
  
  // Extract phone (various international formats)
  const phoneMatch = extractPhoneNumber(trimmedLine);
  if (phoneMatch) {
    member.phone = normalizePhoneToE164(phoneMatch.number, phoneMatch.countryCode, trimmedLine);
  }
  
  // Check if this line is ONLY a phone number or ONLY an email
  const isOnlyPhone = phoneMatch && phoneMatch.originalMatch.trim() === trimmedLine;
  const isOnlyEmail = emailMatch && emailMatch[0].trim() === trimmedLine;
  
  let nameText = trimmedLine;
  
  // If it's only a phone or only an email, don't try to extract name
  if (isOnlyPhone || isOnlyEmail) {
    nameText = '';
  } else {
    // Extract name (everything else, cleaned up)
    if (emailMatch) {
      nameText = nameText.replace(emailMatch[0], '');
    }
    if (phoneMatch && phoneMatch.originalMatch) {
      nameText = nameText.replace(phoneMatch.originalMatch, '');
    }
    
    // Clean up name text (remove common separators and extra spaces)
    nameText = nameText
      .replace(/[,|;]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  
  // Split name into first and last only if we have meaningful name text
  if (nameText && nameText.length > 0) {
    const nameParts = nameText.split(/\s+/).filter(part => part);
    if (nameParts.length >= 1) {
      member.firstName = nameParts[0];
    }
    if (nameParts.length >= 2) {
      member.lastName = nameParts.slice(1).join(' ');
    }
  }
  
  // Create combined name field
  member.name = [member.firstName, member.lastName].filter(Boolean).join(' ');
  
  // Validate we have at least some useful data (phone OR email required, name optional)
  member.isValid = !!(member.phone || member.email);
  member.hasWarnings = !member.email || !member.phone;
  
  return member.isValid ? member : null;
};

// Define specific error types for better user guidance
export class FileParseError extends Error {
  constructor(type, message, suggestions = []) {
    super(message);
    this.name = 'FileParseError';
    this.type = type;
    this.suggestions = suggestions;
  }
}

// Error types for different parsing issues
export const ERROR_TYPES = {
  EMPTY_FILE: 'EMPTY_FILE',
  CORRUPTED_FILE: 'CORRUPTED_FILE',
  UNSUPPORTED_FORMAT: 'UNSUPPORTED_FORMAT',
  NO_DATA_ROWS: 'NO_DATA_ROWS',
  NO_VALID_COLUMNS: 'NO_VALID_COLUMNS',
  NO_CONTACT_INFO: 'NO_CONTACT_INFO',
  EXCEL_NOT_SUPPORTED: 'EXCEL_NOT_SUPPORTED',
  ENCODING_ERROR: 'ENCODING_ERROR',
  MALFORMED_CSV: 'MALFORMED_CSV',
  MALFORMED_VCARD: 'MALFORMED_VCARD'
};

/**
 * Parse any supported file type with comprehensive error handling
 * @param {File} file - File to parse
 * @returns {Promise<Array>} Array of member objects
 */
export const parseFile = async (file) => {
  return new Promise((resolve, reject) => {
    // Basic file validation
    if (!file) {
      reject(new FileParseError(ERROR_TYPES.EMPTY_FILE, 
        'No file selected', 
        ['Please select a file to upload']));
      return;
    }

    if (file.size === 0) {
      reject(new FileParseError(ERROR_TYPES.EMPTY_FILE, 
        'File is empty', 
        ['Please check that your file contains data', 'Try exporting your contacts again']));
      return;
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    const supportedExtensions = ['csv', 'vcf', 'xls', 'xlsx'];
    
    if (!supportedExtensions.includes(fileExtension)) {
      reject(new FileParseError(ERROR_TYPES.UNSUPPORTED_FORMAT, 
        `File format ".${fileExtension}" is not supported`, 
        ['Please use CSV, Excel (.xls/.xlsx), or vCard (.vcf) files', 
         'Export your contacts from your address book or spreadsheet application']));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        
        // Check if file content is empty or corrupted
        if (!content || typeof content !== 'string') {
          reject(new FileParseError(ERROR_TYPES.CORRUPTED_FILE, 
            'File appears to be corrupted or unreadable', 
            ['Try re-downloading or re-exporting the file', 
             'Make sure the file is not password protected', 
             'Check that the file is not currently open in another application']));
          return;
        }

        if (content.trim().length === 0) {
          reject(new FileParseError(ERROR_TYPES.EMPTY_FILE, 
            'File contains no readable content', 
            ['Make sure your file has data in it', 
             'Try copying and pasting your contacts instead']));
          return;
        }

        let parsedData;
        
        try {
          switch (fileExtension) {
            case 'csv':
              parsedData = parseCSVContent(content, file.name);
              break;
            case 'vcf':
              parsedData = parseVCardContent(content, file.name);
              break;
            case 'xls':
            case 'xlsx':
              // Excel files need special handling
              reject(new FileParseError(ERROR_TYPES.EXCEL_NOT_SUPPORTED, 
                'Excel files need to be converted to CSV format', 
                ['Open your Excel file and save it as CSV format (File → Save As → CSV)', 
                 'Or copy the data from Excel and use the "Copy & Paste" import option instead']));
              return;
            default:
              reject(new FileParseError(ERROR_TYPES.UNSUPPORTED_FORMAT, 
                `File format ".${fileExtension}" is not supported`, 
                ['Please use CSV or vCard (.vcf) files', 
                 'Export your contacts in CSV format from your application']));
              return;
          }
        } catch (parseError) {
          // Re-throw FileParseError as-is, wrap other errors
          if (parseError instanceof FileParseError) {
            reject(parseError);
            return;
          }
          
          reject(new FileParseError(ERROR_TYPES.CORRUPTED_FILE, 
            `Error processing ${fileExtension.toUpperCase()} file: ${parseError.message}`, 
            ['Check that your file is properly formatted', 
             'Try re-exporting the file from your source application', 
             'Make sure the file is not corrupted']));
          return;
        }
        
        // Validate parsed results
        if (!parsedData || !Array.isArray(parsedData)) {
          reject(new FileParseError(ERROR_TYPES.NO_DATA_ROWS, 
            'Could not extract any data from the file', 
            ['Make sure your file contains contact information', 
             'Check that the file format matches the file extension', 
             'Try using a different export format']));
          return;
        }

        if (parsedData.length === 0) {
          reject(new FileParseError(ERROR_TYPES.NO_DATA_ROWS, 
            'No contact records found in the file', 
            ['Make sure your file has data rows beyond just headers', 
             'Check that your contacts actually have information filled in', 
             'Try copying and pasting your contacts instead']));
          return;
        }

        // Check if any contacts have useful information
        const validContacts = parsedData.filter(contact => 
          contact.name || contact.email || contact.phone || contact.firstName || contact.lastName
        );

        if (validContacts.length === 0) {
          reject(new FileParseError(ERROR_TYPES.NO_CONTACT_INFO, 
            `Found ${parsedData.length} row(s) but no recognizable contact information`, 
            ['Make sure your file has columns like: Name, Email, Phone, First Name, Last Name', 
             'Check that your data is in the correct columns', 
             'Column headers should be in English', 
             'Try arranging your data with clear column headers']));
          return;
        }

        resolve(parsedData);
      } catch (error) {
        if (error instanceof FileParseError) {
          reject(error);
        } else {
          reject(new FileParseError(ERROR_TYPES.CORRUPTED_FILE, 
            'Unexpected error reading file', 
            ['The file may be corrupted or in an unexpected format', 
             'Try re-downloading or re-exporting the file', 
             'Contact support if the problem persists']));
        }
      }
    };
    
    reader.onerror = () => {
      reject(new FileParseError(ERROR_TYPES.CORRUPTED_FILE, 
        'Failed to read file - file may be corrupted', 
        ['Try re-downloading the file', 
         'Make sure the file is not currently open in another application', 
         'Check that you have permission to read the file']));
    };
    
    // Detect encoding issues and handle appropriately
    reader.readAsText(file, 'UTF-8');
  });
};

/**
 * Parse CSV content with enhanced error detection
 * @param {string} content - CSV file content
 * @param {string} fileName - Original file name for error context
 * @returns {Array} Array of member objects
 */
const parseCSVContent = (content, fileName = 'file') => {
  try {
    const lines = content.split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      throw new FileParseError(ERROR_TYPES.EMPTY_FILE, 
        'CSV file is empty', 
        ['Make sure your CSV file contains data', 'Add at least a header row and one data row']);
    }
    
    if (lines.length === 1) {
      throw new FileParseError(ERROR_TYPES.NO_DATA_ROWS, 
        'CSV file only contains headers, no data rows found', 
        ['Make sure your CSV has data rows below the header row', 
         'Check that your export included the actual contact data, not just column names']);
    }

    // Parse and validate headers
    const headerLine = lines[0];
    let headers;
    
    try {
      headers = parseCSVLine(headerLine).map(h => h.trim().replace(/"/g, ''));
    } catch (error) {
      throw new FileParseError(ERROR_TYPES.MALFORMED_CSV, 
        'Could not parse CSV headers - file may be malformed', 
        ['Check that your CSV uses proper comma separation', 
         'Make sure quotes are properly closed', 
         'Try re-exporting your CSV file']);
    }
    
    if (headers.length === 0) {
      throw new FileParseError(ERROR_TYPES.NO_VALID_COLUMNS, 
        'No column headers found in CSV file', 
        ['Make sure your CSV file has column headers in the first row', 
         'Column headers should be like: Name, Email, Phone, etc.']);
    }

    // Check for recognizable column headers
    const recognizableHeaders = headers.filter(header => {
      const lower = header.toLowerCase();
      return lower.includes('name') || lower.includes('email') || lower.includes('phone') || 
             lower.includes('mobile') || lower.includes('first') || lower.includes('last') ||
             lower.includes('contact') || lower.includes('mail') || lower.includes('tel');
    });

    if (recognizableHeaders.length === 0) {
      throw new FileParseError(ERROR_TYPES.NO_VALID_COLUMNS, 
        `CSV headers don't match expected contact fields. Found: ${headers.slice(0, 5).join(', ')}${headers.length > 5 ? '...' : ''}`, 
        ['Use column headers like: Name, Email, Phone, First Name, Last Name', 
         'Make sure your headers are in English', 
         'Check that your first row contains column names, not data']);
    }
    
    const contacts = [];
    let validRowCount = 0;
    let totalRowCount = 0;
    
    for (let i = 1; i < lines.length; i++) {
      totalRowCount++;
      
      let values;
      try {
        values = parseCSVLine(lines[i]);
      } catch (error) {
        // Skip malformed rows but continue processing
        continue;
      }
      
      // Skip rows that are clearly empty or malformed
      if (values.length === 0 || values.every(val => !val || !val.trim())) {
        continue;
      }
      
      const contact = {
        id: `csv-${i}`,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        rawText: lines[i]
      };
      
      // Map common field names
      headers.forEach((header, index) => {
        const value = values[index]?.trim().replace(/"/g, '') || '';
        if (!value) return;
        
        const lowerHeader = header.toLowerCase();
        
        if (lowerHeader.includes('name') && !lowerHeader.includes('first') && !lowerHeader.includes('last')) {
          contact.name = value;
        } else if (lowerHeader.includes('email') || lowerHeader.includes('mail')) {
          contact.email = value;
        } else if (lowerHeader.includes('phone') || lowerHeader.includes('mobile') || lowerHeader.includes('tel')) {
          const phoneMatch = extractPhoneNumber(value);
          if (phoneMatch) {
            contact.phone = normalizePhoneToE164(phoneMatch.number, phoneMatch.countryCode, lines[i]);
          }
        } else if (lowerHeader.includes('first')) {
          contact.firstName = value;
        } else if (lowerHeader.includes('last')) {
          contact.lastName = value;
        }
      });
      
      // Construct full name if not present
      if (!contact.name && (contact.firstName || contact.lastName)) {
        contact.name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      }
      
      // Split name if we only have full name
      if (contact.name && !contact.firstName && !contact.lastName) {
        const nameParts = contact.name.split(' ');
        contact.firstName = nameParts[0] || '';
        contact.lastName = nameParts.slice(1).join(' ') || '';
      }
      
      // Only add if we have at least a name or email
      if (contact.name || contact.email || contact.phone) {
        contact.isValid = true;
        contact.hasWarnings = !contact.email || !contact.phone;
        contacts.push(contact);
        validRowCount++;
      }
    }
    
    // Provide helpful feedback about data quality
    if (contacts.length === 0) {
      throw new FileParseError(ERROR_TYPES.NO_CONTACT_INFO, 
        `Processed ${totalRowCount} data rows but found no usable contact information`, 
        ['Make sure your data is in the correct columns under the headers', 
         'Check that rows actually contain names, emails, or phone numbers', 
         'Verify that your data isn\'t in a different format than expected']);
    }
    
    return contacts;
    
  } catch (error) {
    if (error instanceof FileParseError) {
      throw error;
    }
    
    throw new FileParseError(ERROR_TYPES.MALFORMED_CSV, 
      'Error parsing CSV file - file may be corrupted or in wrong format', 
      ['Make sure your file is actually in CSV format', 
       'Try opening the file in a text editor to check its contents', 
       'Re-export your data as CSV from your source application']);
  }
};

/**
 * Parse a single CSV line handling quoted values
 * @param {string} line - CSV line
 * @returns {Array} Array of values
 */
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"' && (i === 0 || line[i-1] === ',')) {
      inQuotes = true;
    } else if (char === '"' && inQuotes && (i === line.length - 1 || line[i+1] === ',')) {
      inQuotes = false;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
};

/**
 * Parse vCard content with enhanced error detection
 * @param {string} content - vCard file content
 * @param {string} fileName - Original file name for error context
 * @returns {Array} Array of member objects
 */
const parseVCardContent = (content, fileName = 'file') => {
  try {
    // Check if this looks like a vCard file
    if (!content.includes('BEGIN:VCARD')) {
      throw new FileParseError(ERROR_TYPES.MALFORMED_VCARD, 
        'File does not appear to be a valid vCard format', 
        ['Make sure the file is exported as vCard format (.vcf)', 
         'vCard files should contain "BEGIN:VCARD" markers', 
         'Try exporting your contacts again in vCard format']);
    }

    if (!content.includes('END:VCARD')) {
      throw new FileParseError(ERROR_TYPES.MALFORMED_VCARD, 
        'vCard file appears to be incomplete or corrupted', 
        ['Make sure the file was completely downloaded/exported', 
         'vCard files should have matching BEGIN:VCARD and END:VCARD tags', 
         'Try re-exporting your contacts']);
    }

    const contacts = [];
    const vCards = content.split('END:VCARD');
    let validVCardCount = 0;
    let totalVCardCount = 0;
    
    vCards.forEach((vCardContent, index) => {
      if (!vCardContent.includes('BEGIN:VCARD')) return;
      totalVCardCount++;
      
      try {
        const contact = {
          id: `vcard-${index}`,
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          rawText: 'From vCard file'
        };
        
        const lines = vCardContent.split('\n');
        let hasValidData = false;
        
        lines.forEach(line => {
          line = line.trim();
          if (!line) return;
          
          if (line.startsWith('FN:')) {
            contact.name = line.substring(3).trim();
            if (contact.name) {
              hasValidData = true;
              const nameParts = contact.name.split(' ');
              contact.firstName = nameParts[0] || '';
              contact.lastName = nameParts.slice(1).join(' ') || '';
            }
          } else if (line.startsWith('EMAIL')) {
            const emailMatch = line.match(/:(.*)/);
            if (emailMatch && emailMatch[1].trim()) {
              contact.email = emailMatch[1].trim();
              hasValidData = true;
            }
          } else if (line.startsWith('TEL')) {
            const phoneMatch = line.match(/:(.*)/);
            if (phoneMatch && phoneMatch[1].trim()) {
              const phoneResult = extractPhoneNumber(phoneMatch[1]);
              if (phoneResult) {
                contact.phone = normalizePhoneToE164(phoneResult.number, phoneResult.countryCode, vCardContent);
                hasValidData = true;
              }
            }
          } else if (line.startsWith('N:')) {
            // Format: N:Last;First;Middle;Prefix;Suffix
            const nameParts = line.substring(2).split(';');
            if (nameParts.length >= 2) {
              const lastName = nameParts[0].trim();
              const firstName = nameParts[1].trim();
              if (lastName || firstName) {
                contact.lastName = lastName;
                contact.firstName = firstName;
                if (!contact.name) {
                  contact.name = `${firstName} ${lastName}`.trim();
                }
                hasValidData = true;
              }
            }
          }
        });
        
        // Only add if we have at least a name or email
        if (hasValidData && (contact.name || contact.email || contact.phone)) {
          contact.isValid = true;
          contact.hasWarnings = !contact.email || !contact.phone;
          contacts.push(contact);
          validVCardCount++;
        }
      } catch (error) {
        // Skip individual malformed vCards but continue processing others
        console.warn(`Skipping malformed vCard ${index + 1}:`, error.message);
      }
    });
    
    if (totalVCardCount === 0) {
      throw new FileParseError(ERROR_TYPES.NO_DATA_ROWS, 
        'No vCard entries found in file', 
        ['Make sure your vCard file contains actual contact entries', 
         'Check that the file is properly formatted with BEGIN:VCARD and END:VCARD tags', 
         'Try re-exporting your contacts in vCard format']);
    }

    if (contacts.length === 0) {
      throw new FileParseError(ERROR_TYPES.NO_CONTACT_INFO, 
        `Found ${totalVCardCount} vCard entries but no usable contact information`, 
        ['Make sure your vCards contain names, emails, or phone numbers', 
         'Check that the vCard entries have FN: (name) or EMAIL: or TEL: fields', 
         'Try exporting with more contact details included']);
    }
    
    return contacts;
    
  } catch (error) {
    if (error instanceof FileParseError) {
      throw error;
    }
    
    throw new FileParseError(ERROR_TYPES.MALFORMED_VCARD, 
      'Error parsing vCard file - file may be corrupted or in wrong format', 
      ['Make sure your file is actually in vCard format (.vcf)', 
       'Try opening the file in a text editor to check its contents', 
       'Re-export your contacts as vCard from your source application']);
  }
};